// 函数节流（如果有连续事件响应，则每间隔一定时间段触发）
// 每间隔 wait(Number) milliseconds 触发一次 func 方法
// 如果 options 参数传入 {leading: false}
// 那么不会马上触发（等待 wait milliseconds 后第一次触发 func）
// 如果 options 参数传入 {trailing: false}
// 那么最后一次回调不会被触发
// **Notice: options 不能同时设置 leading 和 trailing 为 false**
// 示例：
// var throttled = _.throttle(updatePosition, 100);
// $(window).scroll(throttled);
// 调用方式（注意看 A 和 B console.log 打印的位置）：
// _.throttle(function, wait, [options])
// sample 1: _.throttle(function(){}, 1000)
// print: A, B, B, B ...
// sample 2: _.throttle(function(){}, 1000, {leading: false})
// print: B, B, B, B ...
// sample 3: _.throttle(function(){}, 1000, {trailing: false})
// print: A, A, A, A ...
// ----------------------------------------- //

export function throttle(func, wait, options) {
  var context, args, result; // setTimeout 的 handler

  var timeout = null; // 标记时间戳
  // 上一次执行回调的时间戳

  var previous = 0; // 如果没有传入 options 参数
  // 则将 options 参数置为空对象

  if (!options) {
    options = {};
  }

  var later = function () {
    // 如果 options.leading === false
    // 则每次触发回调后将 previous 置为 0
    // 否则置为当前时间戳
    previous = options.leading === false ? 0 : Date.now();
    timeout = null; // console.log('B')

    result = func.apply(context, args); // 这里的 timeout 变量一定是 null 了吧
    // 是否没有必要进行判断？

    if (!timeout) {
      context = args = null;
    }
  }; // 以滚轮事件为例（scroll）
  // 每次触发滚轮事件即执行这个返回的方法
  // _.throttle 方法返回的函数


  return function () {
    // 记录当前时间戳
    var now = Date.now(); // 第一次执行回调（此时 previous 为 0，之后 previous 值为上一次时间戳）
    // 并且如果程序设定第一个回调不是立即执行的（options.leading === false）
    // 则将 previous 值（表示上次执行的时间戳）设为 now 的时间戳（第一次触发时）
    // 表示刚执行过，这次就不用执行了

    if (!previous && options.leading === false) {
      previous = now;
    } // 距离下次触发 func 还需要等待的时间


    var remaining = wait - (now - previous);
    context = this;
    args = arguments; // 要么是到了间隔时间了，随即触发方法（remaining <= 0）
    // 要么是没有传入 {leading: false}，且第一次触发回调，即立即触发
    // 此时 previous 为 0，wait - (now - previous) 也满足 <= 0
    // 之后便会把 previous 值迅速置为 now
    // ========= //
    // remaining > wait，表示客户端系统时间被调整过
    // 则马上执行 func 函数
    // @see https://blog.coding.net/blog/the-difference-between-throttle-and-debounce-in-underscorejs
    // ========= //
    // console.log(remaining) 可以打印出来看看

    if (remaining <= 0 || remaining > wait) {
      if (timeout) {
        clearTimeout(timeout); // 解除引用，防止内存泄露

        timeout = null;
      } // 重置前一次触发的时间戳


      previous = now; // 触发方法
      // result 为该方法返回值
      // console.log('A')

      result = func.apply(context, args); // 引用置为空，防止内存泄露
      // 感觉这里的 timeout 肯定是 null 啊？这个 if 判断没必要吧？

      if (!timeout) {
        context = args = null;
      }
    } else if (!timeout && options.trailing !== false) {
      // 最后一次需要触发的情况
      // 如果已经存在一个定时器，则不会进入该 if 分支
      // 如果 {trailing: false}，即最后一次不需要触发了，也不会进入这个分支
      // 间隔 remaining milliseconds 后触发 later 方法
      timeout = setTimeout(later, remaining);
    } // 回调返回值


    return result;
  };
};

// 函数去抖（连续事件触发结束后只触发一次）
// sample 1: _.debounce(function(){}, 1000)
// 连续事件结束后的 1000ms 后触发
// sample 1: _.debounce(function(){}, 1000, true)
// 连续事件触发后立即触发（此时会忽略第二个参数）
export function debounce(func, wait, immediate) {
  var timeout, args, context, timestamp, result;

  var later = function () {
    // 定时器设置的回调 later 方法的触发时间，和连续事件触发的最后一次时间戳的间隔
    // 如果间隔为 wait（或者刚好大于 wait），则触发事件
    var last = Date.now() - timestamp; // 时间间隔 last 在 [0, wait) 中
    // 还没到触发的点，则继续设置定时器
    // last 值应该不会小于 0 吧？

    if (last < wait && last >= 0) {
      timeout = setTimeout(later, wait - last);
    } else {
      // 到了可以触发的时间点
      timeout = null; // 可以触发了
      // 并且不是设置为立即触发的
      // 因为如果是立即触发（callNow），也会进入这个回调中
      // 主要是为了将 timeout 值置为空，使之不影响下次连续事件的触发
      // 如果不是立即执行，随即执行 func 方法

      if (!immediate) {
        // 执行 func 函数
        result = func.apply(context, args); // 这里的 timeout 一定是 null 了吧
        // 感觉这个判断多余了

        if (!timeout) {
          context = args = null;
        }
      }
    }
  }; // 嗯，闭包返回的函数，是可以传入参数的


  return function () {
    // 可以指定 this 指向
    context = this;
    args = arguments; // 每次触发函数，更新时间戳
    // later 方法中取 last 值时用到该变量
    // 判断距离上次触发事件是否已经过了 wait seconds 了
    // 即我们需要距离最后一次触发事件 wait seconds 后触发这个回调方法

    timestamp = Date.now(); // 立即触发需要满足两个条件
    // immediate 参数为 true，并且 timeout 还没设置
    // immediate 参数为 true 是显而易见的
    // 如果去掉 !timeout 的条件，就会一直触发，而不是触发一次
    // 因为第一次触发后已经设置了 timeout，所以根据 timeout 是否为空可以判断是否是首次触发

    var callNow = immediate && !timeout; // 设置 wait seconds 后触发 later 方法
    // 无论是否 callNow（如果是 callNow，也进入 later 方法，去 later 方法中判断是否执行相应回调函数）
    // 在某一段的连续触发中，只会在第一次触发时进入这个 if 分支中

    if (!timeout) // 设置了 timeout，所以以后不会进入这个 if 分支了
    {
      timeout = setTimeout(later, wait);
    } // 如果是立即触发


    if (callNow) {
      // func 可能是有返回值的
      result = func.apply(context, args); // 解除引用

      context = args = null;
    }

    return result;
  };
}
/**
 * @description 深拷贝
 */

export function deepClone(source) {
  if (!source && typeof source !== 'object') {
    throw new Error('error arguments', 'shallowClone');
  }

  const targetObj = source.constructor === Array ? [] : {};
  Object.keys(source).forEach(keys => {
    if (source[keys] && typeof source[keys] === 'object') {
      targetObj[keys] = source[keys].constructor === Array ? [] : {};
      targetObj[keys] = deepClone(source[keys]);
    } else {
      targetObj[keys] = source[keys];
    }
  });
  return targetObj;
}

/**
 * @param {string} path
 * @returns {Boolean}
 */
export function isExternal(path) {
  return /^(https?:|mailto:|tel:)/.test(path)
}

/**
 * @name 获取html元素相对于视窗的位置集合的方法
 * @param obj node节点
 * @returns {Object}
 */
const getObjXy = (obj) => {
  if (!obj) return
  const xy = obj.getBoundingClientRect();
  const top = xy.top - document.documentElement.clientTop + document.documentElement.scrollTop, //document.documentElement.clientTop 在IE67中始终为2，其他高级点的浏览器为0
    bottom = xy.bottom,
    left = xy.left - document.documentElement.clientLeft + document.documentElement.scrollLeft, //document.documentElement.clientLeft 在IE67中始终为2，其他高级点的浏览器为0
    right = xy.right,
    width = xy.width || right - left, //IE67不存在width 使用right - left获得
    height = xy.height || bottom - top;

  return {
    top,
    right,
    bottom,
    left,
    width,
    height
  }
}

/**
 * 随机数
 * @param {*} length
 * @returns {Number}
 */
const randomNumber = (length = 8) => {
  return Number(Math.random().toString().substr(3, length) + Date.now()).toString(36);
}

/**
 * 随机码
 * @param {Number} count
 * @returns {String}
 */
const randomWord = (count = 36) => {
  return Math.random().toString(count).substring(2)
}

/**
 * @name 监听事件
 */
export const addEventListener = {
  listen(targetDomElement, eventName, callback, options = false) {
    // IE 8 以上版本支持
    if (targetDomElement.addEventListener) {
      targetDomElement.addEventListener(eventName, callback, options)
      return {
        remove() {
          targetDomElement.removeEventListener(eventName, callback, options)
        }
      }
    } else if (targetDomElement.attachEvent) {
      targetDomElement.attachEvent('on' + eventName, callback)
      return {
        remove() {
          targetDomElement.detachEvent('on' + eventName, callback)
        }
      }
    }
  }
}

/**
 * 控制台来源 枚举状态
 */
export const CONSOLE_SOURCE_MENU_REL = {
  'MF': {
    id: 0,
    value: 'markingforce'
  },
  'DHR': {
    id: 1,
    value: 'dhr'
  },
  'HANDLE_FIND_HOST_REL': function () {
    let consoleUrlStr = '',
      companyUrl = '',
      isDhr = false
    const {
      host,
    } = window.location
    if (host.indexOf('dhrforce') > 0) {
      isDhr = true
    }
    return {
      isDhr,
    }
  }
}

/**
 * 数据转换 映射关系
 */
export const changeMappingHandle = (mapping = {}, custom = [{
  key: 'code',
  value: 'label',
  returnKey: 'returnKey'
}]) => {
  let list = [],
    o = {},
    keys = {};
  custom.forEach((t, i) => {
    let obj = {}
    for (const key in mapping) {
      const e = mapping[key];
      if (i == 0) {
        list.push(e);
      }
      o[e.code] = e.label;

      obj[e[t['key']]] = e[t['value']];

    }
    keys[t['returnKey']] = obj
  })
  return {
    Array: list,
    Object: mapping,
    SwitchObj: o,
    ...keys
  };
}

export const swapArray = (arr, index1, index2) => {
  arr[index1] = arr.splice(index2, 1, arr[index1])[0];
  return arr;
}

/**
 * @name 复制
 * @param {*} value
 * @param {String} htmlType textarea 复制内容保留换行符
 * @returns
 */
const copy = (value, htmlType = 'input', _this = null) => {
  console.log('value', _this)
  const oInput = document.createElement(htmlType);
  oInput.value = value
  document.body.appendChild(oInput);
  oInput.select();
  document.execCommand("Copy");
  if (window.MODESIGN) {
    window.MODESIGN.Message({
      type: "success",
      message: "复制成功",
    });
  } else {
    _this.$message({
      message: "复制成功",
      type: "success",
    });
  }
  oInput.remove();
}

/**
 * 根据数据条数与每页多少条数据计算页数 
 * totalNum 数据条数
 * limit 每页多少条
 */
const pageCount = (totalNum, limit) => {
    return totalNum > 0 ? ((totalNum < limit) ? 1 : ((totalNum % limit) ? (parseInt(totalNum / limit) + 1) : (
      totalNum /
      limit))) : 0;
  }

/**
 * 休眠
 */
const sleep = (ms, cb = ()=> {}) => {
  let time = null;
  return new Promise(resolve => {
    time = setTimeout(resolve, ms)
    cb(time);
  });
}

export default {
  randomNumber,
  getObjXy,
  CONSOLE_SOURCE_MENU_REL,
  changeMappingHandle,
  randomWord,
  swapArray,
  copy,
  pageCount,
  sleep
}